'use client';

import { useTranslations } from 'next-intl';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';
import { TypographyH2, TypographyLead, TypographyP } from '@/components/ui/typography';
import { Reveal } from '@/components/motion/reveal';
import { serializeJsonLd } from '@/lib/seo-utils';

interface FaqItem {
  question: string;
  answer: string;
}

export function Faq() {
  const t = useTranslations('faq');
  const items = t.raw('items') as FaqItem[];

  // FAQPage structured data must mirror the visible Q&A (Google requirement).
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  };

  return (
    <section id="faq" className="section">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: serializeJsonLd(jsonLd) }}
      />
      <div className="container-brand">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <div className="divider-gold mb-8" />
            <div className="text-center">
              <TypographyH2>{t('heading')}</TypographyH2>
              <TypographyLead className="mx-auto mt-4 max-w-xl">{t('subheading')}</TypographyLead>
            </div>
          </Reveal>

          <Reveal index={1}>
            <Accordion type="single" collapsible defaultValue="item-0" className="mt-10 sm:mt-12">
              {items.map((item, i) => (
                <AccordionItem key={i} value={`item-${i}`}>
                  <AccordionTrigger className="py-6 text-body sm:py-8">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 sm:pb-8">
                    <TypographyP>{item.answer}</TypographyP>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
